import { useTranslation } from "react-i18next";
import { Button } from "@libs/zapi-ui/ds/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { CheckCircleIcon, DotsThreeIcon, PencilSimpleIcon, TrashIcon } from "@phosphor-icons/react";
import { STUB_COMMENT_ID } from "../const/const";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { MouseEvent } from "react";

type TCommentActionsProps = {
  onDelete: (id: string) => void;
  onEdit: () => void;
  commentId: string;
  isCanEdit: boolean;
  withResolveButton?: boolean;
  isVisible: boolean;
};

export function CommentActions(props: TCommentActionsProps) {
  const { onDelete, onEdit, commentId, isCanEdit, withResolveButton, isVisible } = props;
  const { t } = useTranslation(["projectHome", "common"]);

  function handleDelete(event: MouseEvent<HTMLElement>) {
    event.stopPropagation();
    onDelete(commentId);
  }

  function handleEdit(event: MouseEvent<HTMLElement>) {
    event.stopPropagation();
    onEdit();
  }

  return (
    <div
      onClick={(event) => event.stopPropagation()}
      className={cn(
        "flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover/comment:opacity-100 group-focus-within/comment:opacity-100 has-[[data-popup-open]]:opacity-100",
        (isVisible || commentId === STUB_COMMENT_ID) && "opacity-100"
      )}
    >
      {commentId === STUB_COMMENT_ID ? (
        <Spinner aria-label={t("common:loading")} className="size-3.5 text-muted-foreground" />
      ) : (
        <>
          {withResolveButton && (
            <Button
              variant="ghost"
              size="icon-xs"
              shape="pill"
              aria-label={t("comments.resolve")}
              title={t("comments.resolve")}
              onClick={handleDelete}
              data-testid={`comment-${commentId}-resolve-button`}
              className="text-muted-foreground hover:text-success"
            >
              <CheckCircleIcon />
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger
              render={
                <Button
                  variant="ghost"
                  size="icon-xs"
                  shape="pill"
                  aria-label={t("comments.actions")}
                  data-testid={`comment-${commentId}-actions-trigger`}
                  className="text-muted-foreground"
                />
              }
            >
              <DotsThreeIcon weight="bold" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-36">
              {isCanEdit && (
                <DropdownMenuItem onClick={handleEdit} data-testid={`comment-${commentId}-edit`}>
                  <PencilSimpleIcon />
                  {t("comments.edit")}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                variant="destructive"
                onClick={handleDelete}
                data-testid={`comment-${commentId}-delete`}
              >
                <TrashIcon />
                {t("comments.delete")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      )}
    </div>
  );
}
